import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import {
  createOptionsChart,
  IChartApiBase,
  ISeriesApi,
  LineSeries,
  LineStyle,
  LineType,
  Logical,
  Time,
} from "lightweight-charts";
import { saveAs } from "file-saver";
import { v4 as uuidv4 } from "uuid";
import ChartZoomResetButton from "../Charts/ChartZoomResetButton";
import ChartDownloadPngButton from "../Charts/ChartDownloadPngButton";
import { addSeperatorToInput, InputTypes } from "src/helper";
import { Point } from "src/Interfaces/chartInterfaces";
import { toJalaliDateTime, toGregorianDateTime } from "src/Utils/date";
import { getPriceChartOptions } from "../Charts/lwcDefaults";
import { pickSeriesColor } from "../Charts/chartColors";
import { observeChartResize } from "../Charts/observeChartResize";
import { calculateMovingAverageIndicatorValues } from "../Charts/calculateMovingAverageIndicatorValues";
import { createChartRefreshState } from "../Charts/chartRefreshState";

interface Dataset {
  label: string;
  data: Point[];
  borderColor?: string;
  backgroundColor?: string;
  spanGaps?: boolean;
  yAxisID?: string;
  /** Horizontal from/to spans with a vertical jump at joins (old Chart.js step look). */
  stepped?: boolean;
}

interface Props {
  unit?: string;
  datasets: Dataset[];
  className?: string;
  YValueType?: InputTypes;
  showFullDateTime?: boolean;
  /** Category X labels (Jalali date or date+time). Equal spacing, like Chart.js CategoryScale. */
  labels?: string[];
  /** Show Jalali date labels on the X axis (date-based charts like conversion). */
  withDate?: boolean;
  hasSecondaryYAxis?: boolean;
  /** When set, format Y ticks/tooltips with a fixed number of decimal places. */
  decimalPlaces?: number;
  /** Show SMA controls only when the source graphData can support a moving average. */
  allowSma?: boolean;
}

type AnyChart = IChartApiBase<number>;

/** Height for -45° X labels. Full datetime needs more room than HH:MM:SS / date. */
const AXIS_HEIGHT_TIME = 64;
const AXIS_HEIGHT_DATETIME = 110;

const DEFAULT_SMA_PERIOD = 20;
const MIN_SMA_PERIOD = 2;
const MAX_SMA_PERIOD = 500;
const SMA_COLORS = ["#EA580C", "#C026D3", "#0F766E", "#B45309", "#7C3AED"];
const SKIP_SMA_LABELS = new Set(["مرز اجرا"]);

function clampSmaPeriod(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SMA_PERIOD;
  return Math.max(MIN_SMA_PERIOD, Math.min(MAX_SMA_PERIOD, Math.floor(value)));
}

/** Compact large magnitudes: 1.2K / 3.4M / 5.6B / 7.8T */
function formatCompactMagnitude(value: number): string | null {
  const abs = Math.abs(value);
  const units: { threshold: number; suffix: string }[] = [
    { threshold: 1e12, suffix: "T" },
    { threshold: 1e9, suffix: "B" },
    { threshold: 1e6, suffix: "M" },
    { threshold: 1e3, suffix: "K" },
  ];
  for (const { threshold, suffix } of units) {
    if (abs < threshold) continue;
    const scaled = abs / threshold;
    const decimals = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
    let text = scaled.toFixed(decimals).replace(/\.?0+$/, "");
    return `${value < 0 ? "-" : ""}${text}${suffix}`;
  }
  return null;
}

/** Tooltip: exact value, grouped, never compact (2.3M) or parseInt-rounded. */
function formatTooltipNumber(
  value: number,
  YValueType?: InputTypes,
  decimalPlaces?: number,
): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  if (Math.abs(value) < 1e-12) return "0";

  if (YValueType && YValueType !== "price" && YValueType !== "number") {
    const formatted = addSeperatorToInput(value, YValueType);
    if (formatted != null && formatted !== "") return String(formatted);
  }

  const group = (whole: string) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (decimalPlaces != null && Number.isFinite(decimalPlaces)) {
    const text = Number(value).toFixed(decimalPlaces);
    const negative = text.startsWith("-");
    const unsigned = negative ? text.slice(1) : text;
    const [whole, frac] = unsigned.split(".");
    const grouped = group(whole);
    const body = frac != null ? `${grouped}.${frac}` : grouped;
    return negative ? `-${body}` : body;
  }

  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.trunc(abs);
  const grouped = group(String(intPart));
  const frac = abs - intPart;
  if (frac < 1e-12) return sign + grouped;
  const fracStr = abs.toString().split(".")[1];
  if (!fracStr) return sign + grouped;
  return `${sign}${grouped}.${fracStr}`;
}

/** Keep readable precision for charts — never use `printPrice` (it parseInt's). */
function formatChartNumber(
  value: number,
  YValueType?: InputTypes,
  decimalPlaces?: number,
): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  // Collapse float noise around zero so axis/crosshair stay in sync with data
  if (Math.abs(value) < 1e-10) return "0";

  if (decimalPlaces != null && Number.isFinite(decimalPlaces)) {
    return Number(value).toFixed(decimalPlaces);
  }

  if (YValueType && YValueType !== "price" && YValueType !== "number") {
    const formatted = addSeperatorToInput(value, YValueType);
    if (formatted != null && formatted !== "") return String(formatted);
  }

  const compact = formatCompactMagnitude(value);
  if (compact != null) return compact;

  const abs = Math.abs(value);
  let decimals = 2;
  if (abs < 0.0001) decimals = 6;
  else if (abs < 0.01) decimals = 4;
  else if (abs < 1) decimals = 4;

  let text = value.toFixed(decimals);
  if (text.includes(".")) {
    text = text.replace(/\.?0+$/, "");
  }
  return text === "-0" ? "0" : text;
}

function getPriceFormatFromValues(
  values: number[],
  decimalPlaces?: number,
): {
  type: "price";
  precision: number;
  minMove: number;
} {
  if (decimalPlaces != null && Number.isFinite(decimalPlaces)) {
    const precision = Math.max(0, Math.min(8, Math.floor(decimalPlaces)));
    return {
      type: "price",
      precision,
      minMove: Math.pow(10, -precision),
    };
  }

  const finite = values.filter((v) => Number.isFinite(v));
  if (!finite.length) {
    return { type: "price", precision: 2, minMove: 0.01 };
  }

  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const span = Math.abs(max - min);
  const magnitude = Math.max(span, Math.abs(min), Math.abs(max), 1e-6);

  // Pick a step that fits the data range (avoid 1e-8 ticks that create scientific junk)
  let minMove = 0.01;
  if (magnitude < 0.001) minMove = 0.0001;
  else if (magnitude < 0.1) minMove = 0.001;
  else if (magnitude < 10) minMove = 0.01;
  else if (magnitude < 1000) minMove = 0.1;
  else minMove = 1;

  const precision = Math.max(0, Math.min(6, Math.ceil(-Math.log10(minMove))));
  return { type: "price", precision, minMove };
}

/** Keep Y scale readable when a series is flat at ~0 (float-noise zoom).
 *  Do NOT force a ±1 band for flat non-zero series (e.g. Target=0.11) —
 *  that expands the shared price scale and flattens Measurement. */
function makeAutoscaleInfoProvider(values: number[]) {
  const finite = values.filter((v) => Number.isFinite(v));
  const min = finite.length ? Math.min(...finite) : 0;
  const max = finite.length ? Math.max(...finite) : 0;
  const span = Math.abs(max - min);
  const mid = (min + max) / 2;
  const isFlat = finite.length === 0 || span < 1e-6;
  const isNearZero = Math.abs(mid) < 1e-6;

  return (original: () => any) => {
    const base = typeof original === "function" ? original() : null;
    if (!isFlat) return base;

    // Flat but meaningfully non-zero: small relative pad only
    if (!isNearZero) {
      const pad = Math.max(Math.abs(mid) * 0.15, 0.01);
      return {
        ...(base || {}),
        priceRange: {
          minValue: mid - pad,
          maxValue: mid + pad,
        },
      };
    }

    // Flat ~0 (e.g. ChainStatus all zeros): keep a readable ±1 band
    const pad = 1;
    return {
      ...(base || {}),
      priceRange: {
        minValue: mid - pad,
        maxValue: mid + pad,
      },
    };
  };
}

function toUnixSeconds(x: Date | string | number): number | null {
  // Keep millisecond precision so a 1ms step-join still formats as the same HH:mm:ss.
  if (x instanceof Date) {
    const t = x.getTime();
    return Number.isFinite(t) ? t / 1000 : null;
  }
  if (typeof x === "number" && Number.isFinite(x)) {
    return x > 1e12 ? x / 1000 : x;
  }
  if (typeof x === "string") {
    const parsed = Date.parse(x);
    if (Number.isFinite(parsed)) return parsed / 1000;
  }
  return null;
}

function pointToJalaliDate(x: Point["x"]): string | null {
  if (x instanceof Date) {
    if (Number.isNaN(x.getTime())) return null;
    return toJalaliDateTime(x).split(" ")[0] || null;
  }
  if (typeof x === "string") {
    if (/^\d{4}\/\d{2}\/\d{2}/.test(x)) return x.split(" ")[0];
    const d = new Date(x);
    if (!Number.isNaN(d.getTime())) return toJalaliDateTime(d).split(" ")[0];
    return x;
  }
  if (typeof x === "number") {
    const d = new Date(x > 1e12 ? x : x * 1000);
    if (Number.isNaN(d.getTime())) return null;
    return toJalaliDateTime(d).split(" ")[0] || null;
  }
  return null;
}

function formatClockTimeFull(time: number | Time): string {
  let date: Date;
  if (typeof time === "number") {
    date = new Date(time * 1000);
  } else if (typeof time === "string") {
    date = new Date(time);
  } else {
    date = new Date(time.year, time.month - 1, time.day);
  }
  if (Number.isNaN(date.getTime())) return "";
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function formatJalaliLabel(label: string): string {
  return label;
}

function formatAxisTime(
  unixSeconds: number,
  withFullDateTime?: boolean,
): string {
  if (withFullDateTime) {
    return formatJalaliLabel(toJalaliDateTime(new Date(unixSeconds * 1000)));
  }
  return formatClockTimeFull(unixSeconds);
}

/** LWC spaces bars by index, not by time. Map unix seconds onto a linear index grid. */
const MAX_LINEAR_BARS = 8000;
const unixMsKey = (unix: number) => Math.round(unix * 1000);

type LinearTimeScale = {
  tMin: number;
  quantum: number;
  indexCount: number;
  toIndex: (unix: number) => number;
  fromIndex: (idx: number) => number;
};

/**
 * One strictly-increasing index per unique timestamp so LWC never sees
 * duplicate times. Extra bars go to larger real-time gaps so overnight
 * holds stay wide (Chart.js time-scale look).
 */
function buildLinearTimeScale(unixTimes: number[]): LinearTimeScale | null {
  const sorted = Array.from(
    new Set(
      unixTimes
        .filter((t) => Number.isFinite(t))
        .map((t) => unixMsKey(t) / 1000),
    ),
  ).sort((a, b) => a - b);
  if (!sorted.length) return null;

  const tMin = sorted[0];
  const tMax = sorted[sorted.length - 1];
  const span = tMax - tMin;
  const n = sorted.length;
  if (n === 1 || span <= 0) {
    return {
      tMin,
      quantum: 1,
      indexCount: 1,
      toIndex: () => 0,
      fromIndex: () => tMin,
    };
  }

  let minDelta = span;
  for (let i = 1; i < n; i++) {
    const d = sorted[i] - sorted[i - 1];
    if (d > 0 && d < minDelta) minDelta = d;
  }

  const desired = Math.min(
    MAX_LINEAR_BARS,
    Math.max(n, Math.ceil(span / Math.max(minDelta, 0.001))),
  );
  const extraBudget = Math.max(0, desired - n);
  const extras = new Array(n - 1).fill(0);
  if (extraBudget > 0 && span > 0) {
    let used = 0;
    const remainders: { i: number; rem: number }[] = [];
    for (let i = 0; i < n - 1; i++) {
      const raw = (extraBudget * (sorted[i + 1] - sorted[i])) / span;
      extras[i] = Math.floor(raw);
      used += extras[i];
      remainders.push({ i, rem: raw - extras[i] });
    }
    remainders.sort((a, b) => b.rem - a.rem);
    for (let k = 0; k < extraBudget - used && k < remainders.length; k++) {
      extras[remainders[k].i] += 1;
    }
  }

  const indices: number[] = new Array(n);
  const indexByMs = new Map<number, number>();
  indices[0] = 0;
  indexByMs.set(unixMsKey(sorted[0]), 0);
  for (let i = 1; i < n; i++) {
    indices[i] = indices[i - 1] + 1 + extras[i - 1];
    indexByMs.set(unixMsKey(sorted[i]), indices[i]);
  }
  const lastIndex = indices[n - 1];
  const quantum = lastIndex > 0 ? span / lastIndex : 1;

  const toIndex = (unix: number) => {
    const exact = indexByMs.get(unixMsKey(unix));
    if (exact != null) return exact;
    if (unix <= tMin) return 0;
    if (unix >= tMax) return lastIndex;
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (sorted[mid] <= unix) lo = mid;
      else hi = mid;
    }
    const t0 = sorted[lo];
    const t1 = sorted[hi];
    const i0 = indices[lo];
    const i1 = indices[hi];
    if (t1 === t0) return i0;
    return Math.round(i0 + ((unix - t0) / (t1 - t0)) * (i1 - i0));
  };

  const fromIndex = (idx: number) => {
    if (idx <= 0) return tMin;
    if (idx >= lastIndex) return tMax;
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (indices[mid] <= idx) lo = mid;
      else hi = mid;
    }
    const i0 = indices[lo];
    const i1 = indices[hi];
    const t0 = sorted[lo];
    const t1 = sorted[hi];
    if (i1 === i0) return t0;
    return t0 + ((idx - i0) / (i1 - i0)) * (t1 - t0);
  };

  return {
    tMin,
    quantum,
    indexCount: lastIndex + 1,
    toIndex,
    fromIndex,
  };
}

/** LWC Line series: times must be unique integers in strictly ascending order. */
function sanitizeLwcPoints<T extends { time: number; value?: number }>(
  points: T[],
): T[] {
  const sorted = [...points].sort((a, b) => a.time - b.time);
  const out: T[] = [];
  for (const p of sorted) {
    const time = Math.round(Number(p.time));
    if (!Number.isFinite(time)) continue;
    const next = { ...p, time };
    const last = out[out.length - 1];
    if (last && last.time === time) {
      out[out.length - 1] = next;
    } else {
      out.push(next);
    }
  }
  return out;
}

function isNullPointValue(y: unknown): boolean {
  return y == null || (typeof y === "number" && Number.isNaN(y));
}

function hasFiniteValue(p: { value?: number }): boolean {
  return p.value != null && Number.isFinite(p.value);
}

function dedupeByTime<T extends { time: number; value?: number }>(
  points: T[],
): T[] {
  const sorted = [...points].sort((a, b) => a.time - b.time);
  const out: T[] = [];
  for (const p of sorted) {
    const last = out[out.length - 1];
    if (last && last.time === p.time) {
      if (hasFiniteValue(p) || !hasFiniteValue(last)) {
        out[out.length - 1] = p;
      }
    } else {
      out.push(p);
    }
  }
  return out;
}

/**
 * LWC cannot store two values at one time on a single series. A graphPoint
 * join is (toTime, priceA) then (fromTime, priceB) at the same instant.
 * Keep the new price on the step line (so the jump is at that exact X)
 * and plot priceA as a second marker on the same timestamp.
 */
function buildSteppedSeriesData(points: { time: number; value: number }[]): {
  line: { time: number; value: number }[];
  joinMarkers: { time: number; value: number }[];
} {
  const line: { time: number; value: number }[] = [];
  const joinMarkers: { time: number; value: number }[] = [];

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const next = points[i + 1];
    if (
      next &&
      next.time === p.time &&
      Number(next.value) !== Number(p.value)
    ) {
      joinMarkers.push({ time: p.time, value: p.value });
      continue;
    }
    if (line.length && line[line.length - 1].time === p.time) {
      line[line.length - 1] = { time: p.time, value: p.value };
    } else {
      line.push({ time: p.time, value: p.value });
    }
  }
  return { line, joinMarkers };
}

/** Split at nulls so each run is its own series — reliable gaps on LWC options charts. */
function splitContiguousSegments(
  points: { time: number; value?: number }[],
): { time: number; value: number }[][] {
  const segments: { time: number; value: number }[][] = [];
  let current: { time: number; value: number }[] = [];
  for (const p of points) {
    if (p.value == null || Number.isNaN(Number(p.value))) {
      if (current.length) {
        segments.push(current);
        current = [];
      }
      continue;
    }
    current.push({ time: p.time, value: p.value });
  }
  if (current.length) segments.push(current);
  return segments;
}

function pointCategoryKey(x: Point["x"]): string | null {
  if (typeof x === "string") {
    // Keep full "YYYY/MM/DD" or "YYYY/MM/DD HH:mm:ss" — do not strip time.
    // showFullDateTime points must match category labels including the clock.
    return x.includes("-") ? x.replace(/-/g, "/") : x;
  }
  return pointToJalaliDate(x);
}

const X_LABEL_GAP_REM = 2.5;

function remToPx(rem: number): number {
  if (typeof window === "undefined") return rem * 16;
  const base = parseFloat(
    window.getComputedStyle(document.documentElement).fontSize,
  );
  return rem * (Number.isFinite(base) && base > 0 ? base : 16);
}

function gridStepFromBarPx(barPx: number, gapPx: number): number {
  if (!(barPx > 0) || !(gapPx > 0)) return 1;
  return Math.max(1, Math.round(gapPx / barPx));
}

function gridIndices(from: number, to: number, step: number): number[] {
  if (to < from) return [];
  const start = Math.ceil(from / step) * step;
  const result: number[] = [];
  for (let idx = start; idx <= to; idx += step) result.push(idx);
  return result;
}

const TimeseriesChart = ({
  datasets,
  className,
  labels,
  YValueType,
  showFullDateTime,
  withDate,
  hasSecondaryYAxis,
  decimalPlaces,
  allowSma = false,
}: Props) => {
  const [ID] = useState(() => uuidv4());
  const [smaEnabled, setSmaEnabled] = useState(false);
  const [smaPeriodInput, setSmaPeriodInput] = useState(
    String(DEFAULT_SMA_PERIOD),
  );
  const smaPeriod = clampSmaPeriod(Number(smaPeriodInput));
  const smaAvailable = Boolean(allowSma);
  const axisHeight = showFullDateTime ? AXIS_HEIGHT_DATETIME : AXIS_HEIGHT_TIME;

  useEffect(() => {
    if (!smaAvailable && smaEnabled) setSmaEnabled(false);
  }, [smaAvailable, smaEnabled]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const axisRef = useRef<HTMLDivElement | null>(null);
  const axisMeasureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<AnyChart | null>(null);
  const refreshStateRef = useRef(createChartRefreshState());
  const tooltipMoveRef = useRef<(param: any) => void>(() => {});
  const seriesRefs = useRef<
    { series: ISeriesApi<"Line", any>; label: string }[]
  >([]);
  const commentBySeries = useRef<Map<string, Map<number | string, string>>>(
    new Map(),
  );
  const timeLabelBySeries = useRef<Map<string, Map<number | string, string>>>(
    new Map(),
  );
  const valuesAtTimeRef = useRef<
    Map<string, { value: number; series: ISeriesApi<"Line", any> }[]>
  >(new Map());
  const categoryLabelsRef = useRef<string[]>([]);
  const timePointsRef = useRef<number[]>([]);
  const linearTimeRef = useRef<LinearTimeScale | null>(null);
  const indexToUnixRef = useRef<Map<number, number>>(new Map());
  const lastObservedIndexRef = useRef<number | null>(null);
  const labelStepRef = useRef<{ barPx: number; step: number } | null>(null);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    lines: string[];
    placeAbove: boolean;
  } | null>(null);
  const [htmlAxisLabels, setHtmlAxisLabels] = useState<
    { left: number; text: string }[]
  >([]);
  const [isZoomed, setIsZoomed] = useState(false);
  const [hiddenLegendLabels, setHiddenLegendLabels] = useState<Set<string>>(
    () => new Set(),
  );
  const hiddenLegendLabelsRef = useRef(hiddenLegendLabels);
  hiddenLegendLabelsRef.current = hiddenLegendLabels;

  const applyLegendVisibility = () => {
    const hidden = hiddenLegendLabelsRef.current;
    seriesRefs.current.forEach(({ series, label }) => {
      if (!label) return;
      try {
        series.applyOptions({ visible: !hidden.has(label) });
      } catch {
        /* ignore */
      }
    });
  };

  const toggleLegendSeries = (label: string) => {
    setHiddenLegendLabels((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };
  const fittedRangeRef = useRef<{ from: number; to: number } | null>(null);
  const ignoreRangeRef = useRef(false);
  const isZoomedRef = useRef(false);

  const setZoomed = (next: boolean) => {
    isZoomedRef.current = next;
    setIsZoomed((prev) => (prev === next ? prev : next));
  };

  const markAsFitted = () => {
    ignoreRangeRef.current = true;
    requestAnimationFrame(() => {
      const range = chartRef.current?.timeScale().getVisibleLogicalRange();
      fittedRangeRef.current =
        range && Number.isFinite(range.from) && Number.isFinite(range.to)
          ? { from: range.from, to: range.to }
          : null;
      if (chartRef.current) refreshStateRef.current.markFitted(chartRef.current);
      ignoreRangeRef.current = false;
      setZoomed(false);
    });
  };

  const syncZoomedFromChart = () => {
    if (ignoreRangeRef.current) return;
    const current = chartRef.current?.timeScale().getVisibleLogicalRange();
    const fitted = fittedRangeRef.current;
    if (!current || !fitted) {
      setZoomed(false);
      return;
    }
    setZoomed(
      Math.abs(current.from - fitted.from) > 0.8 ||
        Math.abs(current.to - fitted.to) > 0.8,
    );
  };

  const markAsFittedRef = useRef(markAsFitted);
  const syncZoomedFromChartRef = useRef(syncZoomedFromChart);
  markAsFittedRef.current = markAsFitted;
  syncZoomedFromChartRef.current = syncZoomedFromChart;

  const fillParent = Boolean(
    className && /(?:^|\s)h-(?:full|\d+|\[)/.test(String(className)),
  );

  const withDateLabels = useMemo(() => {
    if (!withDate) return [] as string[];
    const set = new Set<string>();
    (datasets || []).forEach((ds) => {
      ds.data?.forEach((p) => {
        const label = pointToJalaliDate(p.x);
        if (label) set.add(label);
      });
    });
    return Array.from(set).sort();
  }, [datasets, withDate]);

  const fullDateTimeLabels = useMemo(() => {
    if (!showFullDateTime) return [] as string[];
    if (labels?.length) return labels;
    const set = new Set<string>();
    (datasets || []).forEach((ds) => {
      ds.data?.forEach((p) => {
        const key = pointCategoryKey(p.x);
        if (key) set.add(key);
      });
    });
    return Array.from(set).sort();
  }, [showFullDateTime, labels, datasets]);

  const categoryLabels = useMemo(() => {
    if (showFullDateTime && fullDateTimeLabels.length)
      return fullDateTimeLabels;
    if (withDate && withDateLabels.length) return withDateLabels;
    return [] as string[];
  }, [showFullDateTime, fullDateTimeLabels, withDate, withDateLabels]);

  const useCategoryAxis = categoryLabels.length > 0;

  const legend = useMemo(() => {
    const items = (datasets || []).map((d, i) => ({
      label: d.label,
      color: d.borderColor || pickSeriesColor(i),
    }));
    if (!smaAvailable || !smaEnabled) return items;

    const sources = (datasets || []).filter(
      (d) => d.label && !SKIP_SMA_LABELS.has(d.label),
    );
    sources.forEach((_, i) => {
      items.push({
        label:
          sources.length === 1
            ? `SMA (${smaPeriod})`
            : `SMA (${smaPeriod}) ${sources[i].label}`,
        color: SMA_COLORS[i % SMA_COLORS.length],
      });
    });
    return items;
  }, [datasets, smaEnabled, smaPeriod, smaAvailable]);

  const refreshHtmlAxis = () => {
    const chart = chartRef.current;
    const el = containerRef.current;
    if (!chart || !el) {
      setHtmlAxisLabels([]);
      return;
    }

    // timeToCoordinate is relative to the plot pane (canvas cell), not the full widget
    let leftOffset = chart.priceScale("left").width();
    if (!leftOffset || leftOffset < 8) {
      const canvasTd = el
        .querySelector("canvas")
        ?.closest("td") as HTMLElement | null;
      leftOffset = canvasTd?.offsetLeft ?? 64;
    }
    const plotWidth = chart.timeScale().width() || el.clientWidth || 1;
    const ticks: { left: number; text: string }[] = [];

    const pushTick = (coord: number | null, text: string) => {
      if (coord == null || Number.isNaN(coord)) return;
      if (coord < -4 || coord > plotWidth + 4) return;
      ticks.push({ left: coord + leftOffset, text });
    };

    const range = chart.timeScale().getVisibleLogicalRange();
    const rangeFrom = range?.from ?? 0;
    const rangeTo = range?.to ?? rangeFrom + 1;
    const span = rangeTo !== rangeFrom ? rangeTo - rangeFrom : 1;
    // Keep custom axis labels on the same pixel coordinates as chart points.
    const logicalX = (idx: number) =>
      chart.timeScale().logicalToCoordinate(idx as Logical);
    const firstBar = logicalX(0);
    const secondBar = logicalX(1);
    const barPx =
      firstBar != null && secondBar != null
        ? Math.abs(secondBar - firstBar)
        : plotWidth / span;
    const gapPx = remToPx(X_LABEL_GAP_REM);
    const endLabelGapPx = (label: string) => {
      axisMeasureCanvasRef.current ??= document.createElement("canvas");
      const context = axisMeasureCanvasRef.current.getContext("2d");
      if (!context) return gapPx;
      context.font = "10px sans-serif";
      // Labels are rotated 45 degrees around their right edge.
      return Math.max(gapPx, (context.measureText(label).width + 10) / Math.SQRT2);
    };
    const prev = labelStepRef.current;
    const nextStep = gridStepFromBarPx(barPx, gapPx);
    const step =
      prev && Math.abs(barPx - prev.barPx) / Math.max(prev.barPx, 1e-6) < 0.08
        ? prev.step
        : nextStep;
    if (
      !prev ||
      prev.step !== step ||
      Math.abs(barPx - prev.barPx) >= 0.08 * Math.max(prev.barPx, 1e-6)
    ) {
      labelStepRef.current = { barPx, step };
    }

    // Use the same time-to-pixel mapping as the series. Interpolating the
    // visible logical range can drift from bar centers and sparse time slots.
    const indexLeft = (idx: number) =>
      (chart as IChartApiBase<number>).timeScale().timeToCoordinate(idx);
    // gridIndices only walks even multiples of `step` from 0, so the true last
    // data point (which the tooltip/crosshair reports) rarely lands on one —
    // force it in, dropping the preceding auto tick only when the labels
    // would be closer than the target gap in screen pixels.
    const withForcedEnd = (
      indices: number[], end: number, endLabel: string,
      coordinate: (index: number) => number | null = logicalX,
    ): number[] => {
      if (!Number.isFinite(end)) return indices;
      if (indices.length && indices[indices.length - 1] === end) return indices;
      const result = [...indices, end];
      if (result.length >= 2) {
        const previousX = coordinate(result[result.length - 2]);
        const endX = coordinate(end);
        if (previousX != null && endX != null && endX - previousX < endLabelGapPx(endLabel)) {
          result.splice(result.length - 2, 1);
        }
      }
      return result;
    };

    if (useCategoryAxis && categoryLabelsRef.current.length) {
      const cats = categoryLabelsRef.current;
      const from = Math.max(0, Math.ceil(rangeFrom));
      const to = Math.min(cats.length - 1, Math.floor(rangeTo));
      if (to < from) {
        setHtmlAxisLabels(ticks);
        return;
      }
      const indices = gridIndices(from, to, step);
      const tickIndices =
        lastObservedIndexRef.current === to
          ? withForcedEnd(indices, to, formatJalaliLabel(cats[to]))
          : indices;
      tickIndices.forEach((idx) => {
        pushTick(logicalX(idx), formatJalaliLabel(cats[idx]));
      });
      setHtmlAxisLabels(ticks);
      return;
    }

    if (linearTimeRef.current) {
      const scale = linearTimeRef.current;
      const from = Math.max(0, Math.ceil(rangeFrom));
      const to = Math.min(scale.indexCount - 1, Math.floor(rangeTo));
      if (to >= from) {
        const endUnix = indexToUnixRef.current.get(to) ?? scale.fromIndex(to);
        const indices = gridIndices(from, to, step);
        const tickIndices =
          lastObservedIndexRef.current === to
            ? withForcedEnd(indices, to, formatAxisTime(endUnix, showFullDateTime), indexLeft)
            : indices;
        tickIndices.forEach((idx) => {
          const unix =
            indexToUnixRef.current.get(idx) ?? scale.fromIndex(idx);
          pushTick(indexLeft(idx), formatAxisTime(unix, showFullDateTime));
        });
      }
    }

    setHtmlAxisLabels(ticks);
  };
  const refreshHtmlAxisRef = useRef(refreshHtmlAxis);
  refreshHtmlAxisRef.current = refreshHtmlAxis;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const refreshState = refreshStateRef.current;

    const leftScale = {
      visible: true,
      borderVisible: true,
      ticksVisible: true,
      minimumWidth: 64,
    } as const;

    // Native X axis hidden — HTML strip is the only X axis (full labels, no truncation)
    const hiddenTimeScale = {
      visible: false,
      borderVisible: false,
      ticksVisible: false,
      minimumHeight: 0,
      rightOffset: 4,
    } as const;

    const chart: AnyChart = createOptionsChart(
      el,
      getPriceChartOptions({
        layout: {
          fontFamily: "Tahoma, Arial, sans-serif",
          fontSize: 11,
        },
        localization: {
          priceFormatter: (price: number) =>
            String(formatChartNumber(price, YValueType, decimalPlaces)),
          timeFormatter: (time: number) => {
            const idx = Math.round(time);
            if (useCategoryAxis) {
              const label = categoryLabelsRef.current[idx];
              return label ? formatJalaliLabel(label) : "";
            }
            const unix =
              indexToUnixRef.current.get(idx) ??
              linearTimeRef.current?.fromIndex(idx);
            return unix != null ? formatAxisTime(unix, showFullDateTime) : "";
          },
        },
        timeScale: {
          ...hiddenTimeScale,
          ...(useCategoryAxis
            ? { barSpacing: 28, minBarSpacing: 8 }
            : { barSpacing: 6, minBarSpacing: 0.01 }),
        },
        leftPriceScale: leftScale,
        rightPriceScale: { visible: false },
      } as any),
    );

    chartRef.current = chart;
    chart.priceScale("left").applyOptions(leftScale);
    chart.priceScale("right").applyOptions({ visible: false });
    chart.timeScale().applyOptions(hiddenTimeScale);

    const onMove = (param: any) => {
      if (
        !param?.point ||
        param.point.x < 0 ||
        param.point.y < 0 ||
        !param.seriesData?.size
      ) {
        setTooltip(null);
        return;
      }

      const hits: {
        series: ISeriesApi<"Line", any>;
        label: string;
        time: number | string;
        value: number;
      }[] = [];
      seriesRefs.current.forEach(({ series, label }) => {
        if (!label || hiddenLegendLabelsRef.current.has(label)) return;
        const raw = param.seriesData.get(series) as
          | { time?: number | string; value?: number }
          | undefined;
        if (!raw || raw.value == null) return;
        hits.push({
          series,
          label,
          time: raw.time as number | string,
          value: raw.value,
        });
        const extras = valuesAtTimeRef.current.get(
          `${label}@${String(raw.time)}`,
        );
        extras?.forEach((extra) => {
          if (
            hits.some(
              (h) =>
                h.label === label &&
                String(h.time) === String(raw.time) &&
                h.value === extra.value,
            )
          ) {
            return;
          }
          hits.push({
            series: extra.series,
            label,
            time: raw.time as number | string,
            value: extra.value,
          });
        });
      });

      // At a toTime/fromTime join both prices share one X — pick the closer Y
      // so the previous interval's tooltip is not replaced by the next one.
      const chosen = new Map<string, (typeof hits)[number]>();
      hits.forEach((hit) => {
        const key = `${hit.label}@${String(hit.time)}`;
        const existing = chosen.get(key);
        if (!existing) {
          chosen.set(key, hit);
          return;
        }
        const yHit = hit.series.priceToCoordinate(hit.value);
        const yExisting = existing.series.priceToCoordinate(existing.value);
        const cursorY = param.point.y;
        if (yHit == null) return;
        if (
          yExisting == null ||
          Math.abs(yHit - cursorY) < Math.abs(yExisting - cursorY)
        ) {
          chosen.set(key, hit);
        }
      });

      const lines: string[] = [];
      const seenLabel = new Set<string>();
      chosen.forEach((hit) => {
        const storedAtTime = valuesAtTimeRef.current.get(
          `${hit.label}@${String(hit.time)}`,
        );
        const exactValue =
          storedAtTime?.find((item) => item.series === hit.series)?.value ??
          storedAtTime?.[0]?.value ??
          hit.value;

        const commentMap = commentBySeries.current.get(hit.label);
        const comment =
          commentMap?.get(`${String(hit.time)}|${exactValue}`) ||
          commentMap?.get(`${String(hit.time)}|${hit.value}`) ||
          commentMap?.get(hit.time) ||
          "";

        const timeLabelMap = timeLabelBySeries.current.get(hit.label);
        let title =
          timeLabelMap?.get(`${String(hit.time)}|${exactValue}`) ||
          timeLabelMap?.get(`${String(hit.time)}|${hit.value}`) ||
          timeLabelMap?.get(hit.time) ||
          "";

        if (!title) {
          if (useCategoryAxis && typeof hit.time === "number") {
            const cat = categoryLabelsRef.current[Math.round(hit.time)];
            title = cat ? formatJalaliLabel(cat) : "";
          } else if (typeof hit.time === "number") {
            const idx = Math.round(hit.time);
            const unix =
              indexToUnixRef.current.get(idx) ??
              linearTimeRef.current?.fromIndex(idx);
            title =
              unix != null ? toGregorianDateTime(new Date(unix * 1000)) : "";
          }
        }

        const formatted = formatTooltipNumber(
          exactValue,
          YValueType,
          decimalPlaces,
        );
        if (title && !lines.some((l) => l === title)) lines.unshift(title);
        if (!seenLabel.has(hit.label)) {
          seenLabel.add(hit.label);
          // Isolate the number so suffixes like T/B/M stay beside the digits in RTL labels.
          lines.push(`${hit.label}: \u2066${formatted}\u2069`);
        }
        if (comment) {
          comment
            .split(/\r?\n|\|/)
            .map((part) => part.trim())
            .filter(Boolean)
            .forEach((part) => lines.push(part));
        }
      });

      if (!lines.length) {
        setTooltip(null);
        return;
      }

      const plotHeight = el.clientHeight || 300;
      const approxTooltipHeight = lines.length * 20 + 24;
      // Keep tooltip above the point when near the bottom so it isn't covered by the X axis
      const placeAbove =
        param.point.y + 12 + approxTooltipHeight > plotHeight - 8;

      setTooltip({
        x: param.point.x,
        y: param.point.y,
        lines,
        placeAbove,
      });
    };

    tooltipMoveRef.current = onMove;
    const handleMove = (param: any) =>
      refreshState.onCrosshairMove(param, onMove);
    chart.subscribeCrosshairMove(handleMove);

    const onRangeChange = () => {
      requestAnimationFrame(() => {
        refreshHtmlAxisRef.current();
        syncZoomedFromChartRef.current();
      });
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(onRangeChange);

    const stopObserving = observeChartResize(el, (width, height) => {
      chart.applyOptions({ width, height });
      if (useCategoryAxis && categoryLabelsRef.current.length) {
        const spacing = Math.max(
          12,
          Math.floor(width / Math.max(categoryLabelsRef.current.length, 1)),
        );
        chart.timeScale().applyOptions({
          barSpacing: Math.min(Math.max(spacing, 12), 48),
        });
      }
      requestAnimationFrame(() => {
        refreshHtmlAxisRef.current();
        if (!isZoomedRef.current) {
          markAsFittedRef.current();
        } else {
          syncZoomedFromChartRef.current();
        }
      });
    });

    return () => {
      stopObserving();
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRangeChange);
      chart.unsubscribeCrosshairMove(handleMove);
      refreshState.reset();
      chart.remove();
      chartRef.current = null;
      seriesRefs.current = [];
      setHtmlAxisLabels([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCategoryAxis, withDate]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    // Capture interaction before removing series or rebuilding the index grid.
    refreshStateRef.current.beginUpdate(chart);
    ignoreRangeRef.current = true;

    seriesRefs.current.forEach(({ series }) => {
      try {
        chart.removeSeries(series);
      } catch {
        /* ignore */
      }
    });
    seriesRefs.current = [];
    commentBySeries.current = new Map();
    timeLabelBySeries.current = new Map();
    valuesAtTimeRef.current = new Map();
    categoryLabelsRef.current = categoryLabels;
    labelStepRef.current = null;
    timePointsRef.current = [];
    indexToUnixRef.current = new Map();
    lastObservedIndexRef.current = null;

    if (!useCategoryAxis && !showFullDateTime) {
      const unix: number[] = [];
      (datasets || []).forEach((ds) => {
        ds.data?.forEach((p) => {
          const t = toUnixSeconds(p.x as any);
          if (t != null) unix.push(t);
        });
      });
      linearTimeRef.current = buildLinearTimeScale(unix);
    } else {
      linearTimeRef.current = null;
    }

    const allTimes = new Set<number>();
    let hasSecondarySeries = false;
    let smaColorIndex = 0;
    const smaSourceCount = (datasets || []).filter(
      (d) => d.label && !SKIP_SMA_LABELS.has(d.label),
    ).length;

    (datasets || []).forEach((dataset, index) => {
      const color = dataset.borderColor || pickSeriesColor(index);
      const useSecondary =
        hasSecondaryYAxis &&
        (dataset.yAxisID === "y1" || dataset.label === "مرز اجرا");
      if (useSecondary) hasSecondarySeries = true;

      const commentMap = new Map<number | string, string>();
      const timeLabelMap = new Map<number | string, string>();
      const rememberMeta = (p: Point, idx: number) => {
        if (p.comment) {
          commentMap.set(idx, p.comment);
          commentMap.set(`${idx}|${p.y}`, p.comment);
        }
        if (p.timeLabel) {
          timeLabelMap.set(idx, p.timeLabel);
          timeLabelMap.set(`${idx}|${p.y}`, p.timeLabel);
        }
      };
      const ordered: { time: number; value?: number }[] = [];

      if (useCategoryAxis) {
        const labelIndex = new Map<string, number>();
        categoryLabels.forEach((label, idx) => {
          labelIndex.set(label, idx);
          const slash = label.includes("-") ? label.replace(/-/g, "/") : label;
          const dash = label.includes("/") ? label.replace(/\//g, "-") : label;
          labelIndex.set(slash, idx);
          labelIndex.set(dash, idx);
        });

        if (showFullDateTime) {
          // Equal slot per distinct date+time (Chart.js category), sparse samples.
          dataset.data?.forEach((p) => {
            const key = pointCategoryKey(p.x);
            if (!key) return;
            const idx = labelIndex.get(key);
            if (idx == null) return;
            if (isNullPointValue(p.y)) {
              ordered.push({ time: idx });
              return;
            }
            ordered.push({ time: idx, value: p.y as number });
            rememberMeta(p, idx);
          });
        } else {
          // Date charts: one equal slot per day; missing days become visible gaps.
          const byLabel = new Map<string, Point>();
          dataset.data?.forEach((p) => {
            const key = pointCategoryKey(p.x);
            if (key) byLabel.set(key, p);
          });

          categoryLabels.forEach((label, idx) => {
            const key = label.includes("-") ? label.replace(/-/g, "/") : label;
            const p = byLabel.get(key) ?? byLabel.get(label);
            if (!p || isNullPointValue(p.y)) {
              ordered.push({ time: idx });
              return;
            }
            ordered.push({ time: idx, value: p.y as number });
            rememberMeta(p, idx);
          });
        }
      } else {
        const scale = linearTimeRef.current;
        dataset.data?.forEach((p) => {
          const t = toUnixSeconds(p.x as any);
          if (t == null) return;
          allTimes.add(t);
          const idx = scale ? scale.toIndex(t) : Math.round(t);
          indexToUnixRef.current.set(idx, t);
          if (isNullPointValue(p.y)) {
            ordered.push({ time: idx });
            return;
          }
          ordered.push({ time: idx, value: p.y as number });
          rememberMeta(p, idx);
        });
      }

      ordered.forEach((point) => {
        if (point.value == null || !Number.isFinite(point.value)) return;
        lastObservedIndexRef.current = Math.max(
          lastObservedIndexRef.current ?? point.time,
          point.time
        );
      });

      const drawStepped = Boolean(dataset.stepped) && !smaAvailable;
      const deduped = drawStepped ? ordered : dedupeByTime(ordered);
      const values = deduped
        .filter((p) => p.value != null && Number.isFinite(p.value))
        .map((p) => p.value as number);
      const priceFormat = getPriceFormatFromValues(values, decimalPlaces);
      const segments = splitContiguousSegments(deduped);

      const mapSeriesPoint = (p: { time: number; value: number }) => ({
        time: p.time,
        value: p.value,
      });

      const rememberValue = (
        label: string,
        time: number,
        value: number,
        series: ISeriesApi<"Line", any>,
      ) => {
        const key = `${label}@${String(time)}`;
        const list = valuesAtTimeRef.current.get(key) || [];
        list.push({ value, series });
        valuesAtTimeRef.current.set(key, list);
      };

      const addSegmentSeries = (data: { time: number; value: number }[]) => {
        const { line, joinMarkers } = drawStepped
          ? buildSteppedSeriesData(data)
          : { line: data, joinMarkers: [] };

        const series = chart.addSeries(LineSeries, {
          color,
          lineWidth: 2,
          lineType: drawStepped ? LineType.WithSteps : LineType.Simple,
          pointMarkersVisible: true,
          crosshairMarkerVisible: true,
          priceLineVisible: false,
          lastValueVisible: false,
          title: "",
          priceScaleId: useSecondary ? "right" : "left",
          priceFormat,
          autoscaleInfoProvider: makeAutoscaleInfoProvider(values),
        });
        series.setData(sanitizeLwcPoints(line.map(mapSeriesPoint)) as any);
        seriesRefs.current.push({ series, label: dataset.label });
        line.forEach((p) =>
          rememberValue(dataset.label, p.time, p.value, series),
        );

        if (joinMarkers.length) {
          const markerSeries = chart.addSeries(LineSeries, {
            color,
            lineVisible: false,
            pointMarkersVisible: true,
            crosshairMarkerVisible: true,
            priceLineVisible: false,
            lastValueVisible: false,
            title: "",
            priceScaleId: useSecondary ? "right" : "left",
            priceFormat,
          });
          markerSeries.setData(
            sanitizeLwcPoints(joinMarkers.map(mapSeriesPoint)) as any,
          );
          seriesRefs.current.push({
            series: markerSeries,
            label: dataset.label,
          });
          joinMarkers.forEach((p) =>
            rememberValue(dataset.label, p.time, p.value, markerSeries),
          );
        }
      };

      if (!segments.length) {
        // No real values — keep empty series for legend consistency
        const series = chart.addSeries(LineSeries, {
          color,
          lineWidth: 2,
          pointMarkersVisible: true,
          priceLineVisible: false,
          lastValueVisible: false,
          title: "",
          priceScaleId: useSecondary ? "right" : "left",
          priceFormat,
        });
        series.setData([]);
        seriesRefs.current.push({ series, label: dataset.label });
      } else {
        // Each non-null run is a separate series → visible gaps like the reference chart
        segments.forEach(addSegmentSeries);
      }

      commentBySeries.current.set(dataset.label, commentMap);
      timeLabelBySeries.current.set(dataset.label, timeLabelMap);

      if (
        smaAvailable &&
        smaEnabled &&
        dataset.label &&
        !SKIP_SMA_LABELS.has(dataset.label)
      ) {
        // Official TradingView Lightweight Charts SMA (indicator-examples).
        const smaSource = drawStepped
          ? segments.flatMap((seg) => buildSteppedSeriesData(seg).line)
          : deduped;
        const smaData = calculateMovingAverageIndicatorValues(smaSource, {
          length: smaPeriod,
          source: "value",
        });
        const hasSmaValue = smaData.some(
          (p) =>
            "value" in p && typeof (p as { value?: number }).value === "number",
        );
        if (hasSmaValue) {
          const smaColor = SMA_COLORS[smaColorIndex % SMA_COLORS.length];
          const smaLabel =
            smaSourceCount === 1
              ? `SMA (${smaPeriod})`
              : `SMA (${smaPeriod}) ${dataset.label}`;
          const smaSeries = chart.addSeries(LineSeries, {
            color: smaColor,
            lineWidth: 2,
            lineStyle: LineStyle.Dashed,
            pointMarkersVisible: false,
            crosshairMarkerVisible: true,
            priceLineVisible: false,
            lastValueVisible: false,
            title: "",
            priceScaleId: useSecondary ? "right" : "left",
            priceFormat,
          });
          smaSeries.setData(sanitizeLwcPoints(smaData as any) as any);
          seriesRefs.current.push({ series: smaSeries, label: smaLabel });
          smaColorIndex += 1;
        }
      }
    });

    chart.priceScale("left").applyOptions({
      visible: true,
      borderVisible: true,
      ticksVisible: true,
      minimumWidth: 64,
    });
    chart.priceScale("right").applyOptions({
      visible: hasSecondarySeries,
      borderVisible: hasSecondarySeries,
      ticksVisible: hasSecondarySeries,
    });
    chart.timeScale().applyOptions({
      visible: false,
      borderVisible: false,
      ticksVisible: false,
      minimumHeight: 0,
    });

    // Reserve empty slots so X spacing follows the index grid (days or linear time).
    if (useCategoryAxis && categoryLabels.length) {
      const scaffold = chart.addSeries(LineSeries, {
        color: "rgba(0,0,0,0)",
        lineVisible: false,
        pointMarkersVisible: false,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
        lastValueVisible: false,
        title: "",
        priceScaleId: "left",
      });
      scaffold.setData(categoryLabels.map((_, idx) => ({ time: idx })) as any);
      seriesRefs.current.push({ series: scaffold, label: "" });
    } else if (linearTimeRef.current && linearTimeRef.current.indexCount > 0) {
      const { indexCount } = linearTimeRef.current;
      const scaffold = chart.addSeries(LineSeries, {
        color: "rgba(0,0,0,0)",
        lineVisible: false,
        pointMarkersVisible: false,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
        lastValueVisible: false,
        title: "",
        priceScaleId: "left",
      });
      scaffold.setData(
        Array.from({ length: indexCount }, (_, idx) => ({ time: idx })) as any,
      );
      seriesRefs.current.push({ series: scaffold, label: "" });
    }

    timePointsRef.current = Array.from(allTimes).sort((a, b) => a - b);

    const el = containerRef.current;
    if (useCategoryAxis && categoryLabels.length && el) {
      const spacing = Math.max(
        12,
        Math.floor(el.clientWidth / Math.max(categoryLabels.length, 1)),
      );
      chart.timeScale().applyOptions({
        barSpacing: Math.min(Math.max(spacing, 12), 48),
      });
    }

    applyLegendVisibility();

    const scale = linearTimeRef.current;
    const axisKeys = useCategoryAxis
      ? categoryLabels
      : scale
        ? Array.from({ length: scale.indexCount }, (_, idx) => scale.fromIndex(idx))
        : timePointsRef.current;
    const preserved = refreshStateRef.current.finishUpdate(
      chart,
      axisKeys,
      seriesRefs.current.map(({ series }) => series),
      tooltipMoveRef.current,
    );
    requestAnimationFrame(() => {
      refreshHtmlAxisRef.current();
      if (preserved) {
        ignoreRangeRef.current = false;
        syncZoomedFromChartRef.current();
      } else {
        markAsFittedRef.current();
      }
    });
  }, [
    datasets,
    categoryLabels,
    useCategoryAxis,
    hasSecondaryYAxis,
    YValueType,
    showFullDateTime,
    withDate,
    decimalPlaces,
    smaEnabled,
    smaPeriod,
    smaAvailable,
  ]);

  useEffect(() => {
    applyLegendVisibility();
  }, [hiddenLegendLabels]);

  const handleResetZoom = (e?: ReactMouseEvent) => {
    e?.stopPropagation();
    const chart = chartRef.current;
    if (!chart) return;
    ignoreRangeRef.current = true;
    chart.timeScale().fitContent();
    chart.priceScale("left").applyOptions({ autoScale: true });
    try {
      chart.priceScale("right").applyOptions({ autoScale: true });
    } catch {
      /* right scale may be unused */
    }
    requestAnimationFrame(() => {
      refreshHtmlAxis();
      markAsFitted();
    });
  };

  const saveCanvas = (e?: ReactMouseEvent) => {
    e?.stopPropagation();
    const chart = chartRef.current;
    if (!chart) return;
    const canvas = chart.takeScreenshot();
    canvas.toBlob((blob) => {
      if (blob) saveAs(blob, "chart.png");
    });
  };

  return (
    <div
      dir="ltr"
      style={{ backgroundColor: "white", direction: "ltr" }}
      onClick={(e) => e.stopPropagation()}
      className={
        fillParent
          ? `${className || ""} relative flex flex-col w-full min-h-0`
          : `relative flex flex-col ${className || ""}`
      }
      id={ID}
    >
      <div className="flex items-center gap-2 px-1 py-1 shrink-0 bg-white">
        <div className="flex items-center gap-2">
          <ChartZoomResetButton isZoomed={isZoomed} onClick={handleResetZoom} />
          <ChartDownloadPngButton onClick={saveCanvas} />
          {smaAvailable ? (
            <>
              <label
                className="inline-flex items-center gap-1.5 text-xs text-gray-700 whitespace-nowrap cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={smaEnabled}
                  onChange={(e) => setSmaEnabled(e.target.checked)}
                />
                SMA
              </label>
              {smaEnabled ? (
                <input
                  type="number"
                  min={MIN_SMA_PERIOD}
                  max={MAX_SMA_PERIOD}
                  value={smaPeriodInput}
                  title="SMA length"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setSmaPeriodInput(e.target.value)}
                  onBlur={() => setSmaPeriodInput(String(smaPeriod))}
                  className="w-14 rounded border border-gray-300 px-1 py-0.5 text-xs"
                />
              ) : null}
            </>
          ) : null}
        </div>
        {legend.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-4 flex-1 text-xs text-gray-700">
            {legend.map((item) => {
              const hidden = hiddenLegendLabels.has(item.label);
              return (
                <span
                  key={item.label}
                  role="button"
                  tabIndex={0}
                  title={hidden ? "نمایش نمودار" : "مخفی کردن نمودار"}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLegendSeries(item.label);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleLegendSeries(item.label);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 cursor-pointer select-none"
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{
                      backgroundColor: item.color,
                      opacity: hidden ? 0.35 : 1,
                    }}
                  />
                  <span className={hidden ? "line-through opacity-50" : ""}>
                    {item.label}
                  </span>
                </span>
              );
            })}
          </div>
        ) : null}
      </div>

      <div
        className={
          fillParent
            ? "relative w-full flex-1 min-h-0 overflow-visible"
            : "relative w-full overflow-visible"
        }
        style={{
          direction: "ltr",
          ...(fillParent ? undefined : { height: 300 }),
        }}
      >
        <div
          ref={containerRef}
          className="absolute inset-0"
          style={{ direction: "ltr" }}
        />
        {tooltip ? (
          <div
            className="pointer-events-none absolute z-50 max-w-xs rounded border border-gray-200 bg-white px-2 py-1 text-xs shadow-md text-left"
            dir="ltr"
            style={{
              left: Math.max(8, tooltip.x + 12),
              top: tooltip.placeAbove
                ? Math.max(8, tooltip.y - 8)
                : tooltip.y + 12,
              transform: tooltip.placeAbove ? "translateY(-100%)" : undefined,
            }}
          >
            {tooltip.lines.map((line, i) => (
              <div key={`${i}-${line}`} className="leading-5">
                {line}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* X axis: -45° labels, end of the text nearest the axis */}
      <div
        ref={axisRef}
        className="relative z-0 w-full shrink-0 bg-white border-t border-gray-200 overflow-hidden"
        style={{
          direction: "ltr",
          height: axisHeight,
          minHeight: axisHeight,
        }}
      >
        {htmlAxisLabels.map((tick) => (
          <span
            key={`${tick.text}-${Math.round(tick.left)}`}
            className="absolute pointer-events-none"
            style={{ left: tick.left, top: 6, width: 0, height: 0 }}
          >
            <span
              className="absolute text-[10px] leading-none text-gray-700 whitespace-nowrap font-sans"
              style={{
                right: 0,
                top: 0,
                transform: "rotate(-45deg)",
                transformOrigin: "top right",
              }}
              title={tick.text}
            >
              {tick.text}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default TimeseriesChart;
